import React, { useState } from 'react';
import { FileGroup } from '../types';
import { formatBytes, formatDate, formatTime } from '../utils';
import { compareOcrResults } from '../ocrComparison';
import { RunCard } from './RunCard';
import { PreviewModal } from './PreviewModal';

interface FileCardProps {
  group: FileGroup;
  expandedFiles: Set<string>;
  expandedRuns: Set<string>;
  isPinned: boolean;
  isProcessing: boolean;
  hasNewResults: boolean;
  onToggleFile: () => void;
  onToggleRun: (runId: string) => void;
  onTogglePin: () => void;
  onRemoveRun: (id: string) => void;
  onRemoveGroup: () => void;
  onRerun: (fileGroupId: string) => void;
}

export function FileCard({
  group, expandedFiles, expandedRuns, isPinned, isProcessing, hasNewResults,
  onToggleFile, onToggleRun, onTogglePin, onRemoveRun, onRemoveGroup, onRerun,
}: FileCardProps) {
  const [showPreview, setShowPreview] = useState(false);
  const expanded = expandedFiles.has(group.fileGroupId);
  const latestRun = group.runs[0];
  const ext = latestRun.fileName.split('.').pop()?.toUpperCase() || 'FILE';

  const bothSucceeded = !latestRun.documentAi.error && !latestRun.deapOcr.error
    && latestRun.documentAi.processed && latestRun.deapOcr.raw;
  const comparison = bothSucceeded
    ? compareOcrResults(latestRun.documentAi.processed, latestRun.deapOcr.raw)
    : null;

  const matchBadgeClass = comparison
    ? comparison.matchPercentage >= 90 ? 'high'
      : comparison.matchPercentage >= 70 ? 'medium'
      : 'low'
    : '';

  // Detect commodity: prefer GCP processed, fallback to DEAP raw
  const commodity: string | null = (() => {
    const gcpVal = latestRun.documentAi.processed?.general?.commodity;
    if (gcpVal) return String(gcpVal).toUpperCase();
    const deapVal = latestRun.deapOcr.raw?.general?.commodity;
    if (deapVal) {
      const v = typeof deapVal === 'object' && deapVal?.value != null ? deapVal.value : deapVal;
      return String(v).toUpperCase();
    }
    return null;
  })();

  return (
    <div className={`result-card${isPinned ? ' pinned' : ''}${hasNewResults ? ' has-new' : ''}`}>
      <div className="card-header" onClick={onToggleFile}>
        <div className="card-header-left">
          <svg
            className={`chevron ${expanded ? 'open' : ''}`}
            width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="#484F58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
          <div className="card-file-info">
            {commodity === 'ZP' && (
              <svg className="commodity-icon gas" width="16" height="16" viewBox="0 0 24 24" fill="#F0883E" stroke="none">
                <path d="M12 2c-4 4-7 8-7 12a7 7 0 0 0 14 0c0-4-3-8-7-12zm0 18a5 5 0 0 1-3-1.5c1.5.5 4-.5 4-3.5 0-2-1.5-3.5-2-4 0 2.5-2 3-2 5a3.5 3.5 0 0 0 3 3z"/>
              </svg>
            )}
            {commodity === 'EE' && (
              <svg className="commodity-icon electricity" width="16" height="16" viewBox="0 0 24 24" fill="#F1E05A" stroke="none">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            )}
            <span className="card-filename">{latestRun.fileName}</span>
            <span className="card-badge">{ext}</span>
            <span className="card-meta">
              {formatBytes(latestRun.fileSize)}  ·  {formatDate(latestRun.timestamp)}
            </span>
            {group.runs.length > 1 && (
              <span className="run-count-badge">{group.runs.length} runs</span>
            )}
          </div>
        </div>
        <div className="card-actions" onClick={e => e.stopPropagation()}>
          {!isProcessing && (
            <>
              {latestRun.documentAi.error ? (
                <span className="run-timing-badge gcp failed">GCP Failed</span>
              ) : latestRun.documentAi.totalTimeMs != null ? (
                <span className="run-timing-badge gcp">GCP {formatTime(latestRun.documentAi.totalTimeMs)}</span>
              ) : null}
              {latestRun.deapOcr.error ? (
                <span className="run-timing-badge deap failed">DEAP Failed</span>
              ) : latestRun.deapOcr.processingTimeMs != null ? (
                <span className="run-timing-badge deap">DEAP {formatTime(latestRun.deapOcr.processingTimeMs)}</span>
              ) : null}
              {comparison && (
                <span className={`match-badge ${matchBadgeClass}`}>
                  Match {comparison.matchPercentage.toFixed(0)}%
                </span>
              )}
            </>
          )}
          {latestRun.fileBlob && (
            <button
              className="btn-ghost"
              onClick={() => setShowPreview(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
              </svg>
              <span>Preview</span>
            </button>
          )}
          {latestRun.fileBlob && (
            <button
              className={`btn-ghost btn-rerun ${isProcessing ? 'processing' : ''}`}
              onClick={() => !isProcessing && onRerun(group.fileGroupId)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={isProcessing ? '#58A6FF' : '#8B949E'}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className={isProcessing ? 'spinner-icon' : ''}
              >
                <path d="M21.5 2v6h-6" /><path d="M21.34 15.57a10 10 0 1 1-.57-8.38L21.5 8" />
              </svg>
            </button>
          )}
          <button
            className={`btn-pin ${isPinned ? 'pinned' : ''}`}
            onClick={onTogglePin}
            title={isPinned ? 'Odepnout' : 'Pripnout (zachovat po refreshi)'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24"
              fill={isPinned ? '#58A6FF' : 'none'}
              stroke={isPinned ? '#58A6FF' : '#8B949E'}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M12 17v5" />
              <path d="M9 2.8l6 0l-1 6.2l3.5 0l-5.5 8l-5.5-8l3.5 0z" />
            </svg>
          </button>
          <button className="btn-trash" onClick={onRemoveGroup}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="file-card-body">
          {isProcessing && (
            <div className="rerun-loading-row">
              <svg className="spinner-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58A6FF" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              <span>Running OCR comparison...</span>
            </div>
          )}
          {group.runs.map(run => (
            <RunCard
              key={run.id}
              result={run}
              expanded={expandedRuns.has(run.id)}
              onToggle={() => onToggleRun(run.id)}
              onRemove={group.runs.length > 1 ? () => onRemoveRun(run.id) : undefined}
            />
          ))}
        </div>
      )}

      {showPreview && latestRun.fileBlob && (
        <PreviewModal
          base64={latestRun.fileBlob}
          fileName={latestRun.fileName}
          mimeType={latestRun.mimeType}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
